import { connect } from 'react-redux';
import { useState, useEffect, useRef, useCallback } from 'react';
import { siteboardAction, logout } from '../actions/defaultActions';
import { 
  UserName, TRIAL_PROJECT_LIMIT, TRIAL_SCHEDULE_LIMIT, TRIAL_ADDITIONAL_USER_LIMIT, TRIAL_DURATION_DAYS, CREATE_STANDARD_SUBSCRIPTION_CHECKOUT, 
  CANCEL_PENDING_STANDARD_SUBSCRIPTION, CREATE_TRIAL_SUBSCRIPTION, SET_WELCOME_DIALOGUE_OPEN,
} from '../actions/types';

import TrialSubscriptionCard from './TrialSubscriptionCard';
import StandardSubscriptionCard from './StandardSubscriptionCard';
import SubscriptionPaymentCard from './SubscriptionPaymentCard';
import ParallaxSection from './ParallaxSection';
import Tooltip from './Tooltip';
import PoweredByBar from './PoweredByBar';

import Logo from '../SiteBoard_Logo';
import ExpandDownIcon from '../icons/expandDownIcon';
import ChecklistIcon from '../icons/checklistIcon';
import BackIcon from '../icons/backIcon';

const SetupSubscription = ({ _users, _userId, _subscriptions, siteboardAction, logout }) => {
  const hasPendingStandardSubscription = () => {
    try {
      return _subscriptions[_users[_userId].ownedSubscriptionId].type === 'Intent_Standard';
    } catch(error) {}
    return false;
  };

  const SUBSCRIPTION_PAGE = 'SUBSCRIPTION_PAGE';
  const CHOOSE_PLAN_PAGE = 'CHOOSE_PLAN_PAGE';
  const [currentPage, setCurrentPage] = useState(hasPendingStandardSubscription() ? SUBSCRIPTION_PAGE : CHOOSE_PLAN_PAGE);
  const containerRef = useRef(null);

  const getUserEmail = useCallback(() => {
    try { return _users[_userId].email; } catch(error) { return ''; }
  }, [_users, _userId]);

  useEffect(() => {
    if(containerRef.current) {
      containerRef.current.scrollTo(0, 0);
    }
  }, [currentPage]);

  const onTrialClicked = () => {
    const onSuccess = () => { 
      siteboardAction(SET_WELCOME_DIALOGUE_OPEN, { isOpen: true }); 
    };
    siteboardAction(CREATE_TRIAL_SUBSCRIPTION, {}, onSuccess);
  };
  const onStandardClicked = () => {
    setCurrentPage(SUBSCRIPTION_PAGE);
  };

  return (
    <div className='bg-[#151515]'>
      <div ref={containerRef} className='relative flex flex-col justify-between w-full h-screen justify-center items-center text-white overflow-y-auto'>
        <div className='relative z-[2] w-full flex justify-between'>
          {/* SiteBoard logo */}
          <div className={'flex items-center gap-x-2 font-bold text-[1.8em] p-2'}>
            <Logo size={'1em'} />
            {process.env.REACT_APP_WEBSITE_NAME}
          </div>
          <div className='max-w-[50vw]'>
            <div className='relative group mt-4 mr-4'>
              <div className='flex items-center gap-2 font-medium break-all text-right cursor-default'>
                <div>{getUserEmail()}</div>
                <div className='group-hover:scale-y-[-1]'><ExpandDownIcon /></div>
              </div>
              <div className='hidden absolute top-full right-0 w-full group-hover:block text-right p-2 animate-up-fade'>
                <button 
                  className='text-center px-4 py-2 rounded-lg bg-[#471cffbf] hover:bg-[#471cff] select-none font-bold'
                  onClick={() => { logout(); }}
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
        {currentPage === CHOOSE_PLAN_PAGE &&
          <div className={'grow relative z-[1] flex flex-col justify-center items-center gap-y-[40px] py-10 w-full text-[1.2em] animate-right-fade'}>
            <div className='font-bold text-[1.8em] sm:text-[2.2em] 2xl:text-[2.6em] text-center leading-[1.3em]'>Choose a Plan to Get Started</div>
            <div className='flex flex-col md:flex-row justify-center gap-y-[20px] gap-x-[20px] w-full px-4'>
              <TrialSubscriptionCard onClicked={onTrialClicked} />
              <StandardSubscriptionCard onClicked={onStandardClicked} />
            </div>
          </div>
        }
        {currentPage === SUBSCRIPTION_PAGE &&
          <div className='grow relative z-[1] animate-right-fade flex flex-col justify-center items-center gap-y-6 px-4 py-12'>
            <h1 className='font-bold text-[1.8em] sm:text-[2.2em] 2xl:text-[2.6em] text-center leading-[1.3em]'>Payment</h1>
            <SubscriptionPaymentCard />
            {/* Back button */}
            <div className='w-full mt-8'>
              <button 
                className='group flex justify-center items-center gap-x-2' 
                title='Go Back'
                onClick={() => { 
                  siteboardAction(CANCEL_PENDING_STANDARD_SUBSCRIPTION, {});
                  setCurrentPage(CHOOSE_PLAN_PAGE); 
                }}
              >
                <div className='group-hover:hidden'><BackIcon color='#a59e9e' /></div>
                <div className='hidden group-hover:block'><BackIcon color='#fff' /></div>
                <div className='text-[#a59e9e] group-hover:text-white'>Back</div>
              </button>
            </div>
          </div>
        }
        <div className='z-[1] w-full'>
          <PoweredByBar />
        </div>
        <div className='z-[0] absolute top-[0vh] left-[0vw] opacity-[1] pointer-events-none max-h-[100vh] overflow-hidden'>
          <ParallaxSection parallaxMultiplier={0.5}>
            <img width='1536' height='1024' className='w-[100vw] min-h-[100vh] object-cover object-top' src='/images/constructionSite3.jpg' alt='a construction site' />
            <div className='absolute bottom-0 left-0 h-[15vh] w-[100vw] bg-gradient-to-t from-[#151515] to-[#15151500]'></div>
          </ParallaxSection>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => { return {
  _users: state.serverSynced.users,
  _userId: state.localPersistent.userId,
  _subscriptions: state.serverSynced.subscriptions,
}};
export default connect(mapStateToProps, { siteboardAction, logout })(SetupSubscription);
